import { type EDiscountStatus, type IAddress } from '@mahawi/eshop-common';
import { EEmailType } from '@mahawi/eshop-common/src/types';

import { type AsyncState } from '../async-state';

export enum ECustomerCouponStatus {
  USED = 'used',
}

export enum ECustomerCouponSource {
  ASSIGNED = 'assigned',
  REDEEMED = 'redeemed',
}

export enum ECustomerEmailDeliveryStatus {
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  FAILED = 'failed',
  UNKNOWN = 'unknown',
}

export interface ICustomerDetailAccount {
  uuid: string;
  email: string;
}

export interface ICustomerDetailAddress {
  uuid: string;
  addressTypeId: number;
  isDefault: boolean;
  address: IAddress;
}

export interface ICustomerDetailInvoice {
  fakturoidNumber: string;
  publicHtmlUrl: string;
  invoiceType: string;
}

export interface ICustomerDetailOrder {
  uuid: string;
  statusCode: string;
  currencyCode: string;
  currencySymbol: string;
  price?: number;
  priceWithoutTax?: number;
  invoices: ICustomerDetailInvoice[];
  createdAt: string;
  isForCompany: boolean;
  leadUUID: string;
  leadLanguageCode: string;
}

export interface ICustomerDetailWatchdog {
  uuid: string;
  productUUID: string;
  productName: string;
  isVerified: boolean;
  createdAt: string;
  languageTypeCode: string;
}

export interface ICustomerDetailLead {
  uuid: string;
  languageCode: string;
}

export interface ICustomerDetailSession {
  uuid: string;
  leadId: number;
  createdAt: string;
  path?: string;
  isBot: boolean;
  ipAddress?: string;
  ua?: object;
}

export interface ICustomerDetailCoupon {
  uuid: string;
  code: string;
  discountTypeCode: string;
  value: number;
  description: string | null;
  status: EDiscountStatus | ECustomerCouponStatus.USED;
  validFrom: Date | null;
  validTo: Date | null;
  notifiedAt?: string;
  source: ECustomerCouponSource;
  usages: ICustomerDetailCouponUsage[];
}

export interface ICustomerDetailCouponUsage {
  orderUUID: string;
  usedAt: string;
}

export interface ICustomerCouponCreate {
  discountType: { uuid: string };
  value: number;
  description: string;
  code?: string;
  validTo: Date | null;
}

export const RESENDABLE_EMAIL_TYPES: EEmailType[] = [
  EEmailType.ORDER_SHIPPED,
  EEmailType.REVIEW_SENT_TO_CUSTOMER,
];

export interface ICustomerDetailEmailProduct {
  uuid: string;
  name: string;
}

export interface ICustomerDetailEmail {
  uuid: string;
  emailTypeCode: string;
  emailTypeName: string;
  recipients: string[];
  orderUUIDs: string[];
  products: ICustomerDetailEmailProduct[];
  createdAt: string;
  openCount: number;
  firstOpenedAt: string | null;
  subject: string | null;
  deliveryStatus: ECustomerEmailDeliveryStatus;
  rejectedRecipients: string[];
  error: string | null;
}

export interface ICustomerDetailEmailPreview {
  uuid: string;
  subject: string;
  from: string;
  html: string;
  text: string;
  createdAt: string;
}

export interface ICustomerDetail {
  uuid: string;
  fakturoidId?: number;
  marketingConsent: boolean;
  marketingConsentAt: string | null;
  marketingConsentSource: string | null;
  deletedAt: string | null;
  accounts: ICustomerDetailAccount[];
  addresses: ICustomerDetailAddress[];
  orders: ICustomerDetailOrder[];
  leads: ICustomerDetailLead[];
  sessions: ICustomerDetailSession[];
  watchdogs: ICustomerDetailWatchdog[];
  coupons: ICustomerDetailCoupon[];
  emails: ICustomerDetailEmail[];
  emailsTotal: number;
}

export interface IFakturoidSyncResult {
  success: boolean;
  fakturoidId?: number;
  error?: string;
}

export interface ICrmCustomerDetailState extends AsyncState {
  customer?: ICustomerDetail | null;
  fakturoidSyncInProcess: boolean;
  fakturoidSyncResult?: IFakturoidSyncResult;
  emailResendInProcess: boolean;
  emailPreviewUUID: string | null;
  emailPreview: ICustomerDetailEmailPreview | null;
  emailPreviewInProcess: boolean;
  couponCreateInProcess: boolean;
}
